import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, CircleHelp, Cloud, GitCompare, Loader2, MessageSquare } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ThreadComposer } from '@/features/vault/components/ReviewThread';
import { DiscussionSheet } from '@/features/vault/components/DiscussionSheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SizedDialog } from '@/components/SizedDialog';
import { VaultDocumentPage, documentIdentityFields } from '@/features/vault/components/VaultDocumentPage';
import { FileEditor } from '@/features/vault/components/FileEditor';
import { VersionDiff } from '@/features/vault/components/VersionDiff';
import { sourceLabel, sourceTypeOf } from '@/features/vault/lib/sourceMeta';
import { toastMutationError } from '@/lib/mutationError';
import {
  useCheckout,
  useDiscardDraft,
  useDraftLock,
  useSaveDraftFile,
  useReplyDraft,
  useSubmitDraft,
  useVaultDraft,
  useVaultDraftDiff,
  useDraftChanges,
} from '@/features/vault/collabHooks';
import { useVaultDocument } from '@/features/vault/hooks';
import type { EditorSaveState } from '@/features/vault/components/docx/DocxEditor';
import { submitBlockReason } from '@/features/vault/submitGate';
import { EMPTY_BASE, onDraftLoaded, onSaved, type SaveBase } from '@/features/vault/saveBase';
import { SendBackNotice, hasSendBackFeedback } from '@/features/vault/components/SendBackNotice';
import { CharacterCount } from '@/components/CharacterCount';
import { LockLostNotice } from '@/features/vault/components/LockLostNotice';
import { EDIT_PAGE_PROBE_MS, lockState, rememberLoss, type LockState } from '@/features/vault/lockState';
import { useIsAdmin, useMe } from '@/features/auth/hooks';
import { COLLAB_MESSAGE_MAX } from '@/features/vault/limits';
import { changeTextForRecord, type ChangeEntry } from '@/features/vault/reviewDecisions';
import type { CommentTarget } from '@/features/vault/collabTypes';

const DocxRedline = lazy(() => import('@/features/vault/components/docx/DocxRedline'));

/** One always-visible autosave indicator for the sticky bar, driven by the editor's live state. */
function SaveStatus({ state }: { state: EditorSaveState }) {
  if (state === 'saving') {
    return (
      <span className="flex items-center gap-1.5 text-[13px] text-muted">
        <Loader2 className="size-3.5 animate-spin" aria-hidden /> Saving…
      </span>
    );
  }
  if (state === 'unsaved') {
    // Edits waiting for the autosave pause. Saying "Saving…" here made every keystroke look
    // like it kicked off a save, and the real save (and its brief pause) blur into typing.
    return (
      <span className="flex items-center gap-1.5 text-[13px] text-muted">
        <Cloud className="size-3.5" aria-hidden /> Unsaved changes
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="flex items-center gap-1.5 text-[13px] font-medium text-state-success-text">
        <Check className="size-3.5" aria-hidden /> All changes saved
      </span>
    );
  }
  if (state === 'too_large') {
    // Stays up (the toast is one-shot) until the document fits again, so nobody reads
    // "Saving…" while their edits are in fact being refused.
    return (
      <span className="flex items-center gap-1.5 text-[13px] font-medium text-state-error-text">
        <AlertTriangle className="size-3.5" aria-hidden /> Not saved — document over the size limit
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-[13px] text-muted">
      <Cloud className="size-3.5" aria-hidden /> Edits save automatically
    </span>
  );
}

export const Route = createFileRoute('/vault_/document/$ref/edit')({ component: EditorPage });

/** On-demand help — the editing steps live in a popover instead of a big box up top. */
function HowEditingWorks() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="text-muted hover:text-navy">
          <CircleHelp className="size-4" /> How editing works
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[24rem]">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
          How editing works
        </p>
        <ul className="space-y-2.5 text-[12.5px] leading-relaxed text-muted">
          <li>
            <span className="font-semibold text-navy">Edit in place.</span> Word (.docx) files open in
            the editor below — no upload. Other file types (PDF, spreadsheets) are replaced with a
            revised copy. The released version stays untouched until a reviewer approves your changes.
          </li>
          <li>
            <span className="font-semibold text-navy">Submit for review.</span> When you&apos;re done,
            add a reason for change at the bottom and Submit — a reviewer checks your changes and
            e-signs to release the next version. Every edit is recorded with your name.
          </li>
        </ul>
      </PopoverContent>
    </Popover>
  );
}

// Every live vault document is file-backed. FileEditor edits .docx in-app (the Word
// editor) and replaces PDF/Excel/other files via re-upload. The released file stays
// untouched until a reviewer approves the change.
// Exported for its test: createFileRoute is mocked there, so the Route object is a stub.
export function EditorPage() {
  const { ref } = useParams({ from: '/vault_/document/$ref/edit' });
  const navigate = useNavigate();
  const draft = useVaultDraft(ref, true);
  const detail = useVaultDocument(ref);
  const diff = useVaultDraftDiff(ref, true);
  const fileSave = useSaveDraftFile();
  // The copy this window's editor LOADED — what every save is declared against. Pinned when the
  // draft first arrives for a working copy and advanced only by this window's own saves; never
  // by a refetch, which after a focus change carries another window's newer stamp (saveBase.ts).
  const saveBase = useRef<SaveBase>(EMPTY_BASE);
  useEffect(() => {
    if (draft.data) {
      saveBase.current = onDraftLoaded(saveBase.current, draft.data.version_id, draft.data.updated_at ?? null);
    }
  }, [draft.data]);
  const submit = useSubmitDraft();
  const discard = useDiscardDraft();
  const checkout = useCheckout();
  const isAdmin = useIsAdmin();
  const me = useMe();
  // Who holds the copy NOW — polled every 5s so a take-over / force-unlock from elsewhere shows up
  // here within seconds instead of on the next hard refresh (while stale saves were still going
  // through). Only once the draft itself has loaded, so we know which copy we opened.
  const lockQ = useDraftLock(ref, !!draft.data, EDIT_PAGE_PROBE_MS);
  const probed: LockState = draft.data ? lockState(draft.data.version_id, lockQ.data) : { lost: false };
  // Latched: the first reason the lock was lost is the one shown (an unlock followed by someone's
  // fresh check-out is still "unlocked", not "took over").
  const [lock, setLock] = useState<LockState>({ lost: false });
  useEffect(() => {
    setLock((prev) => rememberLoss(prev, probed));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- probed is derived; compare by content
  }, [probed.lost, probed.lost ? probed.reason : '', probed.lost ? probed.owner : '', probed.lost ? probed.at : '']);
  const hasChanges = (diff.data ?? []).length > 0;

  const [reason, setReason] = useState('');
  const [submitOpen, setSubmitOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  // The reviewer's comments live in the discussion, and this is where the editor reworks — so
  // the conversation has to be readable here. Without it a send-back arrives with a one-line
  // note and every comment about a specific change is stranded on a page they can't reach.
  const [discussionOpen, setDiscussionOpen] = useState(false);
  // Which change the author is writing about, set by the rail's Comment button. The server's
  // reply endpoint already accepts a change reference, so an author's note lands on the same
  // audited thread the reviewer reads — attached to the change, not floating at the end of it.
  const [commentTarget, setCommentTarget] = useState<CommentTarget | null>(null);
  // How many notes already sit on each change, so the rail can say so. Counts BOTH sides of the
  // conversation: on this screen the author's own notes are replies, and a change they have
  // already written about should not look untouched.
  const thread = draft.data?.review_thread;
  const commentCounts = useMemo(
    () => (thread ?? []).reduce<Record<number, number>>((acc, m) => {
      if (m.revision_id != null) acc[m.revision_id] = (acc[m.revision_id] ?? 0) + 1;
      return acc;
    }, {}),
    [thread],
  );
  // Memoized: the editor keys a DOM effect on this object's identity, and a fresh one per render
  // would churn it on every keystroke of the parent's state.
  // The SAME list the reviewer's rail works from. The author used to see only what the browser's
  // engine found in the body, so the two screens showed different lists of one document and the
  // author's was the incomplete one (2026-10-09).
  // Only once a copy is open. Asked before that, the server has nothing to read and refuses —
  // and the refusal is cached, so checking out would leave the rail empty until a reload.
  const draftChanges = useDraftChanges(ref, draft.data != null);
  const rail = useMemo(() => ({
    commentCounts,
    serverChanges: draftChanges.data?.changes ?? [],
    revisionIds: draftChanges.data?.revision_ids ?? [],
    onComment: (entry: ChangeEntry) => {
      setCommentTarget({
        revisionId: entry.revisionId,
        changeText: changeTextForRecord(entry),
      });
      setDiscussionOpen(true);
    },
  }), [commentCounts, draftChanges.data]);

  // The unsent message, kept across closing the drawer (which unmounts it). A ref, so
  // typing never re-renders this page — see ThreadComposer.
  const threadDraft = useRef('');
  const replyM = useReplyDraft();
  // Live save state: the Word editor reports its own (idle→unsaved→saving→saved); the
  // replace-file path has no autosave, so derive it from the save mutation instead.
  const [docxSaveState, setDocxSaveState] = useState<EditorSaveState>('idle');
  // Discard/Submit navigate away, unmounting the editor; flag it so the editor's unmount
  // flush skips (the draft is gone/in-review → a save would 404/409 and toast "Save failed").
  const skipFinalSaveRef = useRef(false);
  // A lost lock unmounts the editor below; its leaving-page save would be refused anyway (403 —
  // the copy is someone else's now, or gone), so skip it rather than toast "Save failed".
  useEffect(() => {
    if (lock.lost) skipFinalSaveRef.current = true;
  }, [lock.lost]);

  if (draft.isPending) {
    return (
      <VaultDocumentPage title="Editing" subtitle="Loading…" back={{ ref }}>
        <p className="rounded-2xl border border-line bg-white px-6 py-16 text-center text-muted shadow-card">
          Loading…
        </p>
      </VaultDocumentPage>
    );
  }
  if (draft.isError || !draft.data) {
    return (
      <VaultDocumentPage
        title="Editing"
        subtitle="No working copy open — check out a copy first."
        back={{ ref }}
      >
        <p className="rounded-2xl border border-line bg-white px-6 py-16 text-center text-muted shadow-card">
          There is no working copy open on this document. Check one out from the document page to
          start editing.
        </p>
      </VaultDocumentPage>
    );
  }

  // Exclusive lock: only the owner (or an admin) edits. A non-owner who lands here
  // gets a read-only notice instead of an editor that would 403 on every save.
  if (!draft.data.is_mine) {
    return (
      <VaultDocumentPage
        title="Checked out by someone else"
        subtitle="This working copy is exclusive to its owner."
        back={{ ref }}
      >
          <div className="rounded-2xl border border-line bg-white px-6 py-16 text-center shadow-card">
            <p className="font-medium text-navy">
              Checked out by {draft.data.owner ?? 'another editor'}
            </p>
            <p className="mx-auto mt-1 max-w-md text-[13px] text-muted">
              Only the person who checked this out can edit it. From the document page you can take it
              over once it&apos;s been idle (or an admin can force-unlock it).
            </p>
            <Button asChild className="mt-4" variant="outline">
              <Link to="/vault/document/$ref" params={{ ref }}>Back to document</Link>
            </Button>
          </div>
      </VaultDocumentPage>
    );
  }

  if (lock.lost) {
    return (
      <VaultDocumentPage
        title="Working copy no longer yours"
        subtitle="This copy changed hands while you were editing."
        back={{ ref }}
      >
          <LockLostNotice
            refId={ref}
            reason={lock.reason}
            owner={lock.owner}
            at={lock.at}
            nowHeldBy={lock.nowHeldBy}
            isAdmin={isAdmin}
            busy={discard.isPending || checkout.isPending}
            onForceUnlock={() =>
              discard.mutate(ref, {
                onSuccess: () => navigate({ to: '/vault/document/$ref', params: { ref } }),
                onError: (e) => toastMutationError(e, 'Force unlock failed'),
              })
            }
            onCheckout={() =>
              checkout.mutate(ref, {
                // the draft query refetches → this page renders the fresh copy's editor
                onError: (e) => toastMutationError(e, 'Check out failed'),
              })
            }
          />
      </VaultDocumentPage>
    );
  }

  const doc = detail.data;
  const filename = draft.data.filename ?? 'Working copy';
  const isDocx = !!draft.data.filename?.toLowerCase().endsWith('.docx');
  const draftVersionId = draft.data.version_id;
  const saveState: EditorSaveState = isDocx
    ? docxSaveState
    : fileSave.isPending
      ? 'saving'
      : fileSave.isSuccess
        ? 'saved'
        : 'idle';
  // Only the Word editor has a live save state; the replace-file path uploads synchronously.
  const blockReason = isDocx ? submitBlockReason(saveState) : null;
  const doSubmit = () =>
    submit.mutate(
      { ref, reason },
      {
        onSuccess: () => {
          skipFinalSaveRef.current = true;
          toast.success('Submitted for review.');
          navigate({ to: '/vault/document/$ref', params: { ref } });
        },
        onError: (e) => toastMutationError(e, 'Submit failed'),
      },
    );
  const country = doc?.country ?? ref.split(':')[2] ?? null;
  const typeLabel = doc?.doc_type_label ?? sourceLabel(sourceTypeOf(ref));
  // The market is a key FACT, so it lives in the identity row below with the document number
  // and version — naming it in the header too said the same thing twice on one screen.
  const heroSubtitle = typeLabel;

  return (
    <VaultDocumentPage
      title={doc?.product_name ?? filename}
      subtitle={heroSubtitle}
      back={{ ref }}
      aside={<HowEditingWorks />}
      identity={doc ? {
        state: doc.display_state,
        fields: documentIdentityFields({
          documentNumber: doc.document_number,
          versionLabel: doc.version_label,
          country,
        }),
      } : null}
    >
        {hasSendBackFeedback({ note: draft.data.review_note, comments: draft.data.review_comments, decisions: draft.data.review_decisions }) && (
          <SendBackNotice
            note={draft.data.review_note}
            comments={draft.data.review_comments}
            decisions={draft.data.review_decisions}
            thread={draft.data.review_thread}
          />
        )}

        <FileEditor
          refId={ref}
          versionId={draft.data.version_id}
          filename={draft.data.filename}
          contentType={draft.data.content_type}
          authorName={draft.data.owner ?? undefined}
          saving={fileSave.isPending}
          onSaveStateChange={setDocxSaveState}
          rail={rail}
          skipFinalSaveRef={skipFinalSaveRef}
          onSaveFile={(file) =>
            new Promise<void>((resolve, reject) =>
              fileSave.mutate(
                { ref, file, baseUpdatedAt: saveBase.current.updatedAt },
                {
                  onSuccess: (saved) => {
                    saveBase.current = onSaved(saved.version_id, saved.updated_at ?? null);
                    resolve();
                  },
                  onError: (e) => {
                    toastMutationError(e, 'Save failed');
                    reject(e);
                  },
                },
              ),
            )
          }
        />

        {/* Sticky action bar — save status + review / submit / discard stay in reach, so you
            never scroll to the bottom of a long document to act. */}
        <div className="sticky bottom-4 z-20 mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-white/95 px-5 py-3 shadow-hero backdrop-blur">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <SaveStatus state={saveState} />
            <span className="hidden text-line-strong sm:inline">·</span>
            <span className="text-[13px] text-muted">
              {hasChanges ? 'Changes ready to submit' : 'No changes yet'}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {draft.data.review_thread.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted hover:text-navy"
                onClick={() => setDiscussionOpen(true)}
              >
                <MessageSquare className="size-4" /> Discussion ({draft.data.review_thread.length})
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={discard.isPending}
                  className="text-state-error-text hover:bg-state-error-bg hover:text-state-error-accent"
                >
                  Discard
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Discard this working copy?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently deletes your in-progress edits and unlocks the document for a
                    fresh check-out. The released version is not affected. This can&apos;t be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={discard.isPending}>Keep editing</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.preventDefault();
                      discard.mutate(ref, {
                        onSuccess: () => {
                          skipFinalSaveRef.current = true;
                          toast.success('Working copy discarded.');
                          navigate({ to: '/vault/document/$ref', params: { ref } });
                        },
                        onError: (err) => toastMutationError(err, 'Discard failed'),
                      });
                    }}
                    disabled={discard.isPending}
                    className="bg-state-error-accent text-white hover:bg-state-error-accent/90"
                  >
                    {discard.isPending ? 'Discarding…' : 'Discard'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {/* Both act on the SERVER's copy (the redline diffs it; Submit moves it into review), so
                both wait while the editor holds edits the server hasn't accepted — see submitGate. */}
            <Button
              variant="outline"
              size="sm"
              disabled={!hasChanges || !!blockReason}
              title={blockReason ?? undefined}
              onClick={() => setReviewOpen(true)}
            >
              <GitCompare className="size-4" /> Review my changes
            </Button>
            <Button
              size="sm"
              disabled={!hasChanges || !!blockReason}
              title={blockReason ?? (!hasChanges ? 'Make your changes before submitting' : undefined)}
              onClick={() => setSubmitOpen(true)}
            >
              Submit for review
            </Button>
          </div>
        </div>

        {/* The review conversation, and the editor can answer here: a reviewer's comment is
            usually a question, and this is when the editor is working through it. */}
        <DiscussionSheet
          open={discussionOpen}
          onOpenChange={setDiscussionOpen}
          entries={draft.data.review_thread}
          currentEmail={me.data?.email ?? null}
          composer={
            <ThreadComposer
              draftRef={threadDraft}
              name={me.data?.name ?? me.data?.email ?? null}
              pending={replyM.isPending}
              // Which change this note is about, shown BEFORE it is sent — the same
              // confirmation the reviewer gets. Without it you type into a box that does not
              // say what you clicked, and only find out once the message is posted.
              target={commentTarget}
              onClearTarget={() => setCommentTarget(null)}
              onSubmit={(body) =>
                replyM
                  .mutateAsync({ ref, body, target: commentTarget })
                  .then(() => {
                    toast.success('Reply added.');
                    setCommentTarget(null);
                  })
                  .catch((err: unknown) => {
                    toastMutationError(err, 'Reply failed');
                    // Rethrown so the box keeps what was written and it can be sent again.
                    throw err;
                  })
              }
            />
          }
        />

        {/* Submit: a focused step with a short confirmation + the reason for change. */}
        <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
          <SizedDialog size="form">
            <DialogHeader>
              <DialogTitle>Submit for review</DialogTitle>
              <DialogDescription>
                A reviewer checks your changes and e-signs to release the next version. The released
                version stays untouched until then.
              </DialogDescription>
            </DialogHeader>
            <p className="rounded-lg border border-line bg-line-soft/40 px-4 py-3 text-[13px] text-navy">
              Sending your working copy
              {doc?.version_label ? (
                <> (<span className="font-mono font-semibold">{doc.version_label}</span>)</>
              ) : null}{' '}
              for review.
            </p>
            <div className="space-y-1.5">
              <label htmlFor="reason" className="text-[13px] font-medium text-navy">
                Reason for change
              </label>
              {/* A reason for change is a paragraph, not a title: same 4-row resizable box as the
                  reviewer's send-back note, so both sides of the loop write in the same space. */}
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                maxLength={COLLAB_MESSAGE_MAX}
                className="w-full resize-y rounded-md border border-line px-3 py-2 text-[13px] leading-relaxed"
                placeholder="What changed and why"
                autoFocus
              />
              <CharacterCount value={reason} max={COLLAB_MESSAGE_MAX} />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setSubmitOpen(false)} disabled={submit.isPending}>
                Cancel
              </Button>
              <Button onClick={doSubmit} disabled={!reason.trim() || submit.isPending}>
                {submit.isPending ? 'Submitting…' : 'Submit for review'}
              </Button>
            </DialogFooter>
          </SizedDialog>
        </Dialog>

        {/* Review my changes: the native Word redline (docx) or the field/file diff. */}
        <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
          <SizedDialog size="viewer" className="flex flex-col gap-0 p-0">
            <DialogHeader className="border-b border-line px-5 py-3">
              <DialogTitle className="truncate pr-8 text-[15px]">Your changes · {filename}</DialogTitle>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-auto bg-[#eef1f5] p-4">
              {!hasChanges ? (
                <p className="py-16 text-center text-[13px] text-muted">No changes yet.</p>
              ) : isDocx ? (
                <Suspense
                  fallback={
                    <p className="py-16 text-center text-[13px] text-muted">Loading the redline…</p>
                  }
                >
                  <DocxRedline refId={ref} versionId={draftVersionId} />
                </Suspense>
              ) : (
                <VersionDiff entries={diff.data ?? []} releasedLabel="Current" />
              )}
            </div>
          </SizedDialog>
        </Dialog>
    </VaultDocumentPage>
  );
}
